import capitalize from 'lodash/capitalize';
import { useProductsStore } from '#/products/store';
import { ProductWithSpot } from '#/products/types';

/** The raw codes, e.g. `XD+XA`. Kept for tooltips and test ids. */
export function joinProductCodes(product_codes: readonly string[]): string {
  return product_codes.join('+');
}

/**
 * Label for a package spanning several products, shaped like the
 * single-product `getProductName`: `ETH Future + BTC Option - PRDX`.
 * The venue is shown once -- a package belongs to exactly one.
 *
 * Falls back to the raw codes when a code has no product: a label is
 * cosmetic, and the codes still identify the package.
 */
export function getProductCodesDescription(
  product_codes: readonly string[],
): string {
  const products = findProducts(product_codes);
  const [firstProduct] = products ?? [];

  if (products == null || firstProduct == null) {
    return joinProductCodes(product_codes);
  }

  return `${productNames(products)} - ${firstProduct.venue}`;
}

/**
 * `getProductCodesDescription` without the trailing venue, for the
 * views that render the venue on a line of their own — the
 * multi-product counterpart of `getLongNameWithoutVenue`.
 */
export function getProductCodesDescriptionWithoutVenue(
  product_codes: readonly string[],
): string {
  const products = findProducts(product_codes);
  if (products == null) return joinProductCodes(product_codes);
  return productNames(products);
}

function findProducts(
  product_codes: readonly string[],
): ProductWithSpot[] | null {
  const { getProduct } = useProductsStore.getState();
  const products: ProductWithSpot[] = [];

  for (const code of product_codes) {
    const product = getProduct(code);
    if (product == null) return null;
    products.push(product);
  }

  return products;
}

// Margin type is left out to keep the column readable; two products
// that differ only by it therefore read alike here.
function productNames(products: readonly ProductWithSpot[]): string {
  return products
    .map((product) => `${product.currency} ${capitalize(product.kind)}`)
    .join(' + ');
}
